import { useLayoutEffect, useRef, useState } from "react";
import { Popover } from "antd";
import { DsaTagDto } from "../../generated/dashboard-client";
import { DsaTagItemKind, useDsaTags } from "../../hooks/useDsaTags";
import "./DsaTags.scss";

const CHIP_GAP = 4;

interface DsaTagChipsProps {
  itemType: DsaTagItemKind;
  itemId: string;
  align?: "left" | "center";
  maxVisible?: number;
  maxRows?: number;
}

function rowsNeeded(widths: number[], containerWidth: number) {
  let rows = 1;
  let used = 0;
  widths.forEach(w => {
    const width = Math.min(w, containerWidth);
    if (used === 0) {
      used = width;
    } else if (used + CHIP_GAP + width <= containerWidth) {
      used += CHIP_GAP + width;
    } else {
      rows++;
      used = width;
    }
  });
  return rows;
}

function countThatFit(chipWidths: number[], moreChipWidth: number, containerWidth: number, maxRows: number) {
  for (let visible = chipWidths.length; visible > 0; visible--) {
    const widths = chipWidths.slice(0, visible);
    if (visible < chipWidths.length) widths.push(moreChipWidth);
    if (rowsNeeded(widths, containerWidth) <= maxRows) return visible;
  }
  return 1;
}

function DsaTagMoreList({ tags }: { tags: DsaTagDto[] }) {
  const listRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const listLeft = list.getBoundingClientRect().left;
    const widestRow = Array.from(list.children)
      .reduce((max, chip) => Math.max(max, chip.getBoundingClientRect().right - listLeft), 0);
    setWidth(Math.ceil(widestRow));
  }, [tags]);

  return <div ref={listRef} className="dsa-tag-more-list" style={width ? { width } : undefined}>
    {tags.map(t => <span key={t.id} className="dsa-tag">{t.name}</span>)}
  </div>;
}

export default function DsaTagChips({ itemType, itemId, align = "center", maxVisible = 3, maxRows }: DsaTagChipsProps) {
  const { tagsFor } = useDsaTags();
  const itemTags = tagsFor(itemType, itemId);
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [fitCount, setFitCount] = useState(itemTags.length);
  const tagKey = itemTags.map(t => `${t.id}:${t.name}`).join("|");

  useLayoutEffect(() => {
    if (!maxRows || !containerRef.current) return;
    const observer = new ResizeObserver(entries => setContainerWidth(entries[0].contentRect.width));
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [maxRows]);

  useLayoutEffect(() => {
    if (!maxRows || !measureRef.current || containerWidth === 0) return;
    const nodes = Array.from(measureRef.current.children) as HTMLElement[];
    const chipWidths = nodes.slice(0, itemTags.length).map(n => n.offsetWidth);
    const moreChipWidth = nodes[itemTags.length]?.offsetWidth ?? 0;
    setFitCount(countThatFit(chipWidths, moreChipWidth, containerWidth, maxRows));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxRows, containerWidth, tagKey]);

  if (itemTags.length === 0) return null;

  const visibleCount = maxRows ? Math.min(fitCount, itemTags.length) : maxVisible;
  const visible = itemTags.slice(0, visibleCount);
  const hidden = itemTags.slice(visibleCount);

  return <div ref={containerRef} className={`dsa-tag-chips ${align}`}>
    {visible.map(t => <span key={t.id} className="dsa-tag" title={t.name}>{t.name}</span>)}
    {hidden.length > 0 && <Popover
      placement="bottom"
      overlayClassName="dsa-tag-more-popover"
      content={<DsaTagMoreList tags={hidden} />}
    >
      <span className="dsa-tag more">+{hidden.length}</span>
    </Popover>}
    {maxRows && <div ref={measureRef} className="dsa-tag-chips dsa-tag-measure" aria-hidden>
      {itemTags.map(t => <span key={t.id} className="dsa-tag">{t.name}</span>)}
      <span className="dsa-tag more">+{itemTags.length}</span>
    </div>}
  </div>;
}
